/**
 * Storefront-only currency display.
 *
 * Shopify remains the source of truth for cart and checkout totals (QAR). This
 * converts only rendered theme price text after a customer selects a country.
 */
(() => {
  const currencies = {
    BH: { code: 'BHD', rate: 0.102 },
    KW: { code: 'KWD', rate: 0.084 },
    OM: { code: 'OMR', rate: 0.105 },
    QA: { code: 'QAR', rate: 1 },
    SA: { code: 'SAR', rate: 1.03 },
    AE: { code: 'AED', rate: 1 },
  };
  const storedCountry = (() => {
    try {
      return window.localStorage.getItem('byf-visual-currency-country');
    } catch {
      return null;
    }
  })();
  let currency = currencies[storedCountry] || currencies[window.BYFVisualCurrencyCountry] || currencies.QA;
  const originalTextNodes = new WeakMap();

  const priceSelectors = [
    'product-price',
    '.sticky-add-to-cart__price',
    '.price',
    '.compare-at-price',
    '.price-per-item__text',
    '.volume-pricing-info__price',
    '.hotspot-dialog__product-price',
    '.cart-items__price',
    '.cart-items__unit-price-wrapper',
    '.cart-totals__original-value',
    '.cart-totals__total-value',
    '.quick-order-list',
    '.unit-price',
  ].join(',');

  const parseQarAmount = (value) => {
    const compactValue = value.replace(/\s/g, '');
    const lastDot = compactValue.lastIndexOf('.');
    const lastComma = compactValue.lastIndexOf(',');
    const decimalIndex = Math.max(lastDot, lastComma);

    if (decimalIndex > -1 && compactValue.length - decimalIndex - 1 <= 2) {
      return Number(
        `${compactValue.slice(0, decimalIndex).replace(/[.,]/g, '')}.${compactValue.slice(decimalIndex + 1)}`
      );
    }

    return Number(compactValue.replace(/[.,]/g, ''));
  };

  const formatAmount = (amount) =>
    new Intl.NumberFormat(document.documentElement.lang === 'ar' ? 'ar-QA' : 'en', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  const displayCurrencyCode = () =>
    document.documentElement.lang === 'ar' && currency.code === 'QAR' ? 'ر.ق.' : currency.code;

  const convertTextNode = (node) => {
    const originalText = originalTextNodes.get(node) || node.nodeValue;
    if (!originalText || !originalText.includes('QAR')) return;

    originalTextNodes.set(node, originalText);

    const convertedText = originalText.replace(/QAR\s*([0-9][0-9.,\s]*)/g, (match, rawAmount) => {
      const amount = parseQarAmount(rawAmount);
      if (!Number.isFinite(amount)) return match;

      if (currency.code === 'QAR') return document.documentElement.lang === 'ar' ? `${displayCurrencyCode()} ${formatAmount(amount)}` : match;
      return `${displayCurrencyCode()} ${formatAmount(amount * currency.rate)}`;
    });

    // Do not create a DOM mutation when the visible value is already current.
    // This prevents the observer below from triggering itself repeatedly.
    if (node.nodeValue !== convertedText) node.nodeValue = convertedText;
  };

  const convertPrices = (root = document) => {
    const containers = [];
    if (root instanceof Element && root.matches(priceSelectors)) containers.push(root);
    if (root.querySelectorAll) containers.push(...root.querySelectorAll(priceSelectors));

    containers.forEach((container) => {
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
      const textNodes = [];
      while (walker.nextNode()) textNodes.push(walker.currentNode);
      textNodes.forEach(convertTextNode);
    });
  };

  const updateCurrencyLabels = () => {
    document.querySelectorAll('.currency-code, [data-testid="localization-currency-code"]').forEach((label) => {
      label.textContent = displayCurrencyCode();
    });
  };

  const updateCountryFlags = (country) => {
    const selectedCountryFlag = document.querySelector(`[data-value="${country}"] .country-flag`);
    if (!selectedCountryFlag) return;

    const flagImage = window.getComputedStyle(selectedCountryFlag).backgroundImage;
    if (!flagImage || flagImage === 'none') return;

    document.querySelectorAll('.icon-flag').forEach((flag) => {
      flag.style.backgroundImage = flagImage;
    });
  };

  // Match the current English messages when saved Arabic translations are stale.
  const updateArabicAnnouncement = (root = document) => {
    if (document.documentElement.lang !== 'ar') return;

    const announcements = [];
    if (root instanceof Element && root.matches('.announcement-bar__text')) announcements.push(root);
    if (root.querySelectorAll) announcements.push(...root.querySelectorAll('.announcement-bar__text'));

    announcements.forEach((announcement) => {
      const text = announcement.textContent.trim();
      if (['تشكيلة ربيع 2026 الجديدة', 'تشكيلة صيف 2026 الجديدة', 'Order your abaya'].includes(text)) {
        announcement.textContent = 'اطلبي عبايتك';
      } else if (['اكتشف أناقة BYF', 'Delivery from the same day up to 5 days'].includes(text)) {
        announcement.textContent = 'التوصيل من نفس اليوم وحتى 5 أيام';
      }
    });
  };

  const updateArabicProductBenefit = (root = document) => {
    if (document.documentElement.lang !== 'ar') return;

    const benefits = [];
    if (root instanceof Element && root.matches('.product-content-grid__text')) benefits.push(root);
    if (root.querySelectorAll) benefits.push(...root.querySelectorAll('.product-content-grid__text'));

    benefits.forEach((benefit) => {
      if (benefit.textContent.includes('تعويض مناخي مجاني')) {
        benefit.textContent = 'عبايات مصممة بعناية';
      }
    });
  };

  const selectCurrency = (country) => {
    currency = currencies[country] || currencies.QA;
    try {
      window.localStorage.setItem('byf-visual-currency-country', country);
    } catch {
      // The selected visual currency still works for this page if storage is unavailable.
    }
    updateCurrencyLabels();
    updateCountryFlags(country);
    convertPrices();
  };

  const start = () => {
    updateCurrencyLabels();
    updateCountryFlags(storedCountry || window.BYFVisualCurrencyCountry);
    convertPrices();
    updateArabicAnnouncement();
    updateArabicProductBenefit();
    window.addEventListener('load', () => {
      updateCurrencyLabels();
      convertPrices();
      updateArabicAnnouncement();
      updateArabicProductBenefit();
    }, { once: true });

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            convertPrices(node);
            updateArabicAnnouncement(node);
            updateArabicProductBenefit(node);
          } else if (node.nodeType === Node.TEXT_NODE && node.parentElement?.closest(priceSelectors)) {
            convertTextNode(node);
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('byf:visual-currency-change', (event) => selectCurrency(event.detail?.country));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
